import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import React from "react";
import {useForm} from "react-hook-form";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {z} from "zod";

import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";
import {PublicPageShell} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {LOGO_URL_DESKTOP} from "@/src/modules/core/utils/constants";

import {PasswordInput} from "../components";
import {useReset} from "../hooks";
import {resetPasswordSchema} from "../schemas";

const Reset = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const form = useForm<z.infer<typeof resetPasswordSchema>>({
    values: {
      token: token ?? "",
      password: "",
      password_confirm: "",
    },
    resolver: zodResolver(resetPasswordSchema),
  });

  const {mutateAsync: reset, isPending} = useReset();

  const handleSubmit = async (data: z.infer<typeof resetPasswordSchema>) => {
    try {
      await reset(data);

      toast.success("Password reset successfully");
      navigate("/login");
    } catch (error) {
      //
    }
  };

  return (
    <PublicPageShell>
      <div className="rounded-lg bg-background p-6 shadow-lg sm:p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center size-16 rounded-full ">
              <img src={LOGO_URL_DESKTOP} alt="Image" height="100" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">STARSPRO</h1>
            <p className="text-muted-foreground">Security Management System</p>
          </div>

          <h2 className="text-xl font-semibold text-slate-800 mb-6 text-center">
            Reset your password
          </h2>

          <Form {...form}>
            <form id="login-form" className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
              {/* <div className="space-y-2">
              <Label htmlFor="user_name" className="text-slate-700">
                Token
              </Label>
              <Input
                id="token"
                type="text"
                placeholder="Enter your token"
                className="border-slate-300"
                {...register("token", {
                  required: "Token is required.",
                })}
                disabled={isPending}
              />
              {errors.token && <FormError error={errors.token.message} />}
            </div> */}
              <FormField
                control={form.control}
                name="password"
                render={({field, formState}) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <PasswordInput {...field} disabled={isPending} />
                    </FormControl>
                    {formState.errors.password && (
                      <FormError error={formState.errors.password.message} />
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password_confirm"
                render={({field, formState}) => (
                  <FormItem>
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <PasswordInput {...field} disabled={isPending} />
                    </FormControl>
                    {formState.errors.password_confirm && (
                      <FormError error={formState.errors.password_confirm.message} />
                    )}
                  </FormItem>
                )}
              />

              {/* <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password_confirm" className="text-slate-700">
                  Confirm Password
                </Label>
              </div>
              <Input
                id="password_confirm"
                type="password"
                placeholder="••••••••"
                className="border-slate-300"
                {...register("password_confirm", {
                  required: "Confirmation password is required.",
                  validate: (value, formData) => {
                    if (value !== formData.password) {
                      return "Passwords do not match";
                    }
                  },
                })}
                disabled={isPending}
              />
              {errors.password_confirm && <FormError error={errors.password_confirm.message} />}
            </div> */}

              <Button
                type="submit"
                className={cn(
                  "w-full bg-primary hover:bg-primary/90",
                  isPending && "opacity-80 cursor-not-allowed",
                )}
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    Resetting...
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  </>
                ) : (
                  "Reset"
                )}
              </Button>
              <div className="flex justify-center">
                <Link to="/login" className="text-sm text-slate-600 hover:underline">
                  Back to login
                </Link>
              </div>
            </form>
          </Form>
        </div>

      <div className="mt-6 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} STARSPRO. All rights reserved.
      </div>
    </PublicPageShell>
  );
};

export default Reset;
